import { FormEvent, useEffect, useMemo, useState } from "react";
import {
  CalendarDays,
  Clock3,
  Plus,
  RefreshCw,
  Scissors,
  Users,
} from "lucide-react";
import { request } from "./FunctionalApp";
const api = async (path: string, init: RequestInit = {}) => {
  const r = await request(path, init),
    x = await r.json().catch(() => ({}));
  if (!r.ok)
    throw new Error(String(x.error || "Unable to save").replaceAll("_", " "));
  return x;
};
const send = (path: string, body: unknown, method = "POST") =>
  api(path, {
    method,
    headers: { "content-type": "application/json" },
    body: JSON.stringify(body),
  });
const pounds = (p: number) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(
    (p || 0) / 100,
  );
const initialTime = () => {
  const d = new Date(Date.now() + 86400000);
  d.setMinutes(0, 0, 0);
  return d.toISOString().slice(0, 16);
};
export function AppointmentsWorkspace() {
  const [data, setData] = useState<any>({
      staff: [],
      services: [],
      assignments: [],
      hours: [],
      blocks: [],
      settings: {},
      businessSettings: { location_limit: 1 },
      customers: [],
      appointments: [],
      users: [],
      locations: [],
      socialProfiles: [],
      socialVideos: [],
    }),
    [tab, setTab] = useState("Calendar"),
    [message, setMessage] = useState(""),
    [loading, setLoading] = useState(true);
  async function load() {
    setLoading(true);
    try {
      setData(await api("/api/appointments/bootstrap"));
    } catch (e: any) {
      setMessage(e.message);
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => {
    load();
  }, []);
  const today = new Date().toISOString().slice(0, 10),
    future = useMemo(
      () =>
        data.appointments.filter(
          (a: any) => String(a.starts_at).slice(0, 10) >= today,
        ),
      [data.appointments, today],
    ),
    tabs = [
      "Calendar",
      ...(data.canBook ? ["New booking"] : []),
      ...(data.canManage ? ["Staff", "Services", "Reports", "Settings"] : []),
    ];
  return (
    <>
      <div className="func-heading">
        <div>
          <h1>Appointments</h1>
          <p>Staff calendars, services, availability and customer bookings.</p>
        </div>
        <button onClick={load}>
          <RefreshCw />
          Refresh
        </button>
      </div>
      {message ? <div className="func-admin-note">{message}</div> : null}
      <div className="appointment-tabs">
        {tabs.map((n) => (
          <button
            className={tab === n ? "selected" : ""}
            onClick={() => setTab(n)}
            key={n}
          >
            {n}
          </button>
        ))}
      </div>
      {loading ? (
        <p className="func-empty">Loading appointments…</p>
      ) : tab === "Calendar" ? (
        <Calendar
          appointments={future}
          staff={data.staff}
          canManage={data.canManage}
          update={async (id: string, status: string) => {
            await send(`/api/appointments/${id}/status`, { status }, "PATCH");
            setMessage("Appointment updated.");
            load();
          }}
        />
      ) : tab === "New booking" ? (
        <NewBooking
          data={data}
          saved={() => {
            setMessage("Appointment created.");
            setTab("Calendar");
            load();
          }}
        />
      ) : tab === "Staff" ? (
        <Staff
          data={data}
          saved={() => {
            setMessage("Staff availability saved.");
            load();
          }}
        />
      ) : tab === "Services" ? (
        <Services
          data={data}
          saved={() => {
            setMessage("Service saved.");
            load();
          }}
        />
      ) : tab === "Reports" ? (
        <Reports data={data} />
      ) : (
        <Settings
          data={data}
          settings={data.settings}
          saved={() => {
            setMessage("Booking settings saved.");
            load();
          }}
        />
      )}
    </>
  );
}
function Calendar({ appointments, staff, canManage, update }: any) {
  const [view, setView] = useState(canManage ? "month" : "agenda");
  const [staffId, setStaffId] = useState("");
  const [month, setMonth] = useState(() => { const d = new Date(); d.setDate(1); d.setHours(12, 0, 0, 0); return d; });
  const visible = appointments.filter((a: any) => !staffId || a.staff_id === staffId);
  const monthAppointments = visible.filter((a: any) => { const d = new Date(a.starts_at); return d.getFullYear() === month.getFullYear() && d.getMonth() === month.getMonth(); });
  const firstWeekday = new Date(month.getFullYear(), month.getMonth(), 1).getDay();
  const mondayOffset = (firstWeekday + 6) % 7;
  const days = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const cells = Array.from({ length: Math.ceil((mondayOffset + days) / 7) * 7 }, (_, index) => index - mondayOffset + 1);
  const dateKey = (date: Date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
  function moveMonth(offset: number) { setMonth(new Date(month.getFullYear(), month.getMonth() + offset, 1, 12)); }
  return (
    <section className="func-panel appointment-calendar">
      <div className="func-table-head">
        <h2>
          <CalendarDays />
          {view === "month" ? "Monthly calendar" : "Upcoming calendar"}
        </h2>
        <div className="appointment-calendar-controls">
          {canManage ? <select value={staffId} onChange={(e) => setStaffId(e.target.value)}><option value="">All staff</option>{staff.map((person: any) => <option value={person.id} key={person.id}>{person.name}</option>)}</select> : null}
          <button className={view === "month" ? "selected" : ""} onClick={() => setView("month")}>Month</button>
          <button className={view === "agenda" ? "selected" : ""} onClick={() => setView("agenda")}>Agenda</button>
        </div>
      </div>
      {view === "month" ? <>
        <div className="appointment-month-nav"><button onClick={() => moveMonth(-1)}>← Previous</button><h3>{month.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</h3><button onClick={() => moveMonth(1)}>Next →</button></div>
        <div className="appointment-month-scroll"><div className="appointment-month-weekdays">{["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map(day => <b key={day}>{day}</b>)}</div>
        <div className="appointment-month-grid">{cells.map((day, index) => {
          if (day < 1 || day > days) return <div className="outside" key={index} />;
          const key = dateKey(new Date(month.getFullYear(), month.getMonth(), day));
          const jobs = monthAppointments.filter((a: any) => String(a.starts_at).slice(0,10) === key);
          const today = key === dateKey(new Date());
          return <div className={today ? "today" : ""} key={key}><strong>{day}</strong>{jobs.map((a: any) => <article className="appointment-month-event" key={a.id}><time>{new Date(a.starts_at).toLocaleTimeString("en-GB", { hour:"2-digit", minute:"2-digit" })}</time><b>{a.customer_name || "Walk-in"}</b><small>{a.staff_name} · {a.service_name}</small></article>)}</div>;
        })}</div></div>
      </> : visible.length ? (
        visible.map((a: any) => (
          <article key={a.id}>
            <time>
              <b>
                {new Date(a.starts_at).toLocaleDateString("en-GB", {
                  weekday: "short",
                  day: "numeric",
                  month: "short",
                })}
              </b>
              <strong>
                {new Date(a.starts_at).toLocaleTimeString("en-GB", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </strong>
            </time>
            <span>
              <b>{a.customer_name || "Walk-in customer"}</b>
              <small>
                {a.service_name} with {a.staff_name}
              </small>
            </span>
            <span>
              <b>{pounds(a.original_price_pence - a.discount_pence)}</b>
              <small>{String(a.payment_status).replaceAll("_", " ")}</small>
            </span>
            <select
              value={a.status}
              onChange={(e) => update(a.id, e.target.value)}
            >
              <option>CONFIRMED</option>
              <option>CHECKED_IN</option>
              <option>IN_PROGRESS</option>
              <option>COMPLETED</option>
              <option>CANCELLED</option>
              <option>NO_SHOW</option>
            </select>
          </article>
        ))
      ) : (
        <p className="func-empty">No upcoming appointments.</p>
      )}
    </section>
  );
}
function NewBooking({ data, saved }: any) {
  const [form, setForm] = useState({
      customerName: "",
      customerEmail: "",
      customerPhone: "",
      serviceId: data.services[0]?.id || "",
      staffId: "",
      startsAt: initialTime(),
      source: "PHONE",
      paymentChoice: "PAY_ON_DAY",
      notes: "",
    }),
    [slots, setSlots] = useState<any[]>([]),
    [error, setError] = useState("");
  const qualified = data.staff.filter((s: any) =>
    data.assignments.some(
      (a: any) => a.staff_id === s.id && a.service_id === form.serviceId,
    ),
  );
  async function find() {
    try {
      const q = new URLSearchParams({
        serviceId: form.serviceId,
        date: form.startsAt.slice(0, 10),
        ...(form.staffId ? { staffId: form.staffId } : {}),
      });
      setSlots((await api(`/api/appointments/slots?${q}`)).slots);
    } catch (e: any) {
      setError(e.message);
    }
  }
  async function submit(e: FormEvent) {
    e.preventDefault();
    try {
      await send("/api/appointments", {
        ...form,
        startsAt: new Date(form.startsAt).toISOString(),
      });
      saved();
    } catch (x: any) {
      setError(x.message);
    }
  }
  return (
    <section className="func-panel">
      <div className="func-table-head">
        <h2>
          <Plus />
          Create appointment or walk-in
        </h2>
      </div>
      <form className="appointment-form" onSubmit={submit}>
        <label>
          Customer name
          <input
            required
            value={form.customerName}
            onChange={(e) => setForm({ ...form, customerName: e.target.value })}
          />
        </label>
        <label>
          Email
          <input
            type="email"
            value={form.customerEmail}
            onChange={(e) =>
              setForm({ ...form, customerEmail: e.target.value })
            }
          />
        </label>
        <label>
          Phone
          <input
            value={form.customerPhone}
            onChange={(e) =>
              setForm({ ...form, customerPhone: e.target.value })
            }
          />
        </label>
        <label>
          Service
          <select
            required
            value={form.serviceId}
            onChange={(e) =>
              setForm({ ...form, serviceId: e.target.value, staffId: "" })
            }
          >
            <option value="">Choose service</option>
            {data.services.map((s: any) => (
              <option value={s.id} key={s.id}>
                {s.name} — {pounds(s.price_pence)}
              </option>
            ))}
          </select>
        </label>
        <label>
          Staff
          <select
            value={form.staffId}
            onChange={(e) => setForm({ ...form, staffId: e.target.value })}
          >
            <option value="">Any available staff</option>
            {qualified.map((s: any) => (
              <option value={s.id} key={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Date and time
          <input
            type="datetime-local"
            required
            value={form.startsAt}
            onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
          />
        </label>
        <label>
          Source
          <select
            value={form.source}
            onChange={(e) => setForm({ ...form, source: e.target.value })}
          >
            <option>PHONE</option>
            <option>IN_PERSON</option>
            <option>WALK_IN</option>
            <option>ONLINE</option>
          </select>
        </label>
        <label>
          Payment
          <select
            value={form.paymentChoice}
            onChange={(e) =>
              setForm({ ...form, paymentChoice: e.target.value })
            }
          >
            <option value="PAY_ON_DAY">Pay on day</option>
            <option value="DEPOSIT">Deposit online</option>
            <option value="FULL_ONLINE">Pay online in full</option>
          </select>
        </label>
        <label className="wide">
          Notes
          <textarea
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </label>
        <div className="wide appointment-actions">
          <button type="button" className="secondary" onClick={find}>
            <Clock3 />
            Check availability
          </button>
          <button disabled={!form.staffId}>Create appointment</button>
        </div>
        {error ? <p className="wide auth-error">{error}</p> : null}
      </form>
      {slots.length ? (
        <div className="slot-list">
          {slots.slice(0, 24).map((s) => (
            <button
              type="button"
              key={`${s.staffId}-${s.startsAt}`}
              onClick={() =>
                setForm({
                  ...form,
                  staffId: s.staffId,
                  startsAt: new Date(s.startsAt).toISOString().slice(0, 16),
                })
              }
            >
              {new Date(s.startsAt).toLocaleTimeString("en-GB", {
                hour: "2-digit",
                minute: "2-digit",
              })}
              <small>{s.staffName}</small>
            </button>
          ))}
        </div>
      ) : null}
    </section>
  );
}
function Staff({ data, saved }: any) {
  const [form, setForm] = useState({
      name: "",
      jobTitle: "",
      bookable: true,
      userId: null as string | null,
    }),
    [selected, setSelected] = useState(""),
    [serviceId, setServiceId] = useState(""),
    [override, setOverride] = useState({ price: "", duration: "" }),
    [schedule, setSchedule] = useState({ days: [1, 2, 3, 4, 5], start: "09:00", end: "17:00" }),
    [block, setBlock] = useState({
      startsAt: initialTime(),
      endsAt: initialTime(),
      kind: "TIME_OFF",
      note: "",
    });
  async function create(e: FormEvent) {
    e.preventDefault();
    await send("/api/appointments/staff", form);
    setForm({ name: "", jobTitle: "", bookable: true, userId: null });
    saved();
  }
  async function assign() {
    if (!selected || !serviceId) return;
    await send(
      `/api/appointments/staff/${selected}/services/${serviceId}`,
      {
        pricePenceOverride: override.price
          ? Math.round(Number(override.price) * 100)
          : null,
        durationMinutesOverride: override.duration
          ? Number(override.duration)
          : null,
      },
      "PUT",
    );
    saved();
  }
  async function saveHours() {
    if (!selected || !schedule.days.length) return;
    await send(`/api/appointments/staff/${selected}/hours`, { hours: schedule.days.map((weekday) => ({ weekday, start: schedule.start, end: schedule.end })) }, "PUT");
    saved();
  }
  async function addBlock() {
    if (!selected) return;
    await send(`/api/appointments/staff/${selected}/blocks`, {
      ...block,
      startsAt: new Date(block.startsAt).toISOString(),
      endsAt: new Date(block.endsAt).toISOString(),
    });
    saved();
  }
  return (
    <div className="appointment-columns">
      <section className="func-panel">
        <div className="func-table-head">
          <h2>
            <Users />
            Staff and time off
          </h2>
        </div>
        {data.staff.map((s: any) => (
          <article key={s.id}>
            <span>
              <b>{s.name}</b>
              <small>{s.job_title || "Bookable staff"}</small>
            </span>
            <em>
              {data.assignments.filter((a: any) => a.staff_id === s.id).length}{" "}
              services
            </em>
          </article>
        ))}
        {data.blocks.map((b: any) => (
          <article key={b.id}>
            <span>
              <b>
                {b.staff_name}: {String(b.kind).replaceAll("_", " ")}
              </b>
              <small>
                {new Date(b.starts_at).toLocaleString("en-GB")} –{" "}
                {new Date(b.ends_at).toLocaleString("en-GB")}
              </small>
            </span>
            <button
              className="secondary"
              onClick={async () => {
                await api(`/api/appointments/blocks/${b.id}`, {
                  method: "DELETE",
                });
                saved();
              }}
            >
              Remove
            </button>
          </article>
        ))}
      </section>
      <section className="func-panel">
        <div className="func-table-head">
          <h2>Add staff</h2>
        </div>
        <form className="appointment-form single" onSubmit={create}>
          <label>
            Name
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label>
            Job title
            <input
              value={form.jobTitle}
              onChange={(e) => setForm({ ...form, jobTitle: e.target.value })}
            />
          </label>
          <label>
            Optional login account
            <select
              value={form.userId || ""}
              onChange={(e) =>
                setForm({ ...form, userId: e.target.value || null })
              }
            >
              <option value="">No login linked</option>
              {(data.users || []).map((u: any) => (
                <option value={u.id} key={u.id}>
                  {u.name} · {u.email}
                </option>
              ))}
            </select>
          </label>
          <button>Add staff member</button>
        </form>
        <div className="appointment-assign">
          <h3>Assign service</h3>
          <select
            value={selected}
            onChange={(e) => setSelected(e.target.value)}
          >
            <option value="">Choose staff</option>
            {data.staff.map((s: any) => (
              <option value={s.id} key={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <select
            value={serviceId}
            onChange={(e) => setServiceId(e.target.value)}
          >
            <option value="">Choose service</option>
            {data.services.map((s: any) => (
              <option value={s.id} key={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          <input
            type="number"
            placeholder="Override price £"
            value={override.price}
            onChange={(e) =>
              setOverride({ ...override, price: e.target.value })
            }
          />
          <input
            type="number"
            placeholder="Override minutes"
            value={override.duration}
            onChange={(e) =>
              setOverride({ ...override, duration: e.target.value })
            }
          />
          <button onClick={assign}>Assign</button>
          <h3>Regular working hours</h3>
          <div className="schedule-days">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((name, weekday) => (
              <label key={name}>
                <input type="checkbox" checked={schedule.days.includes(weekday)} onChange={() => setSchedule({ ...schedule, days: schedule.days.includes(weekday) ? schedule.days.filter((d) => d !== weekday) : [...schedule.days, weekday].sort() })} />
                {name}
              </label>
            ))}
          </div>
          <label>Starts<input type="time" value={schedule.start} onChange={(e) => setSchedule({ ...schedule, start: e.target.value })} /></label>
          <label>Finishes<input type="time" value={schedule.end} onChange={(e) => setSchedule({ ...schedule, end: e.target.value })} /></label>
          <button onClick={saveHours}>Save regular hours</button>
          <h3>Block time off</h3>
          <input
            type="datetime-local"
            value={block.startsAt}
            onChange={(e) => setBlock({ ...block, startsAt: e.target.value })}
          />
          <input
            type="datetime-local"
            value={block.endsAt}
            onChange={(e) => setBlock({ ...block, endsAt: e.target.value })}
          />
          <select
            value={block.kind}
            onChange={(e) => setBlock({ ...block, kind: e.target.value })}
          >
            <option>TIME_OFF</option>
            <option>BREAK</option>
            <option>EXTRA_SHIFT</option>
          </select>
          <input
            placeholder="Reason (optional)"
            value={block.note}
            onChange={(e) => setBlock({ ...block, note: e.target.value })}
          />
          <button onClick={addBlock}>Add calendar block</button>
        </div>
      </section>
    </div>
  );
}
function Services({ data, saved }: any) {
  const [form, setForm] = useState({
    name: "",
    description: "",
    durationMinutes: 45,
    bufferMinutes: 0,
    pricePence: 3500,
    depositType: "NONE",
    depositValue: 0,
    onlineDiscountType: "NONE",
    onlineDiscountValue: 0,
  });
  async function create(e: FormEvent) {
    e.preventDefault();
    await send("/api/appointments/services", form);
    setForm({ ...form, name: "", description: "" });
    saved();
  }
  return (
    <div className="appointment-columns">
      <section className="func-panel">
        <div className="func-table-head">
          <h2>
            <Scissors />
            Services
          </h2>
        </div>
        {data.services.map((s: any) => (
          <article key={s.id}>
            <span>
              <b>{s.name}</b>
              <small>
                {s.duration_minutes} minutes + {s.buffer_minutes} buffer
              </small>
            </span>
            <em>{pounds(s.price_pence)}</em>
          </article>
        ))}
      </section>
      <section className="func-panel">
        <div className="func-table-head">
          <h2>Add service</h2>
        </div>
        <form className="appointment-form" onSubmit={create}>
          <label>
            Name
            <input
              required
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </label>
          <label>
            Price (£)
            <input
              type="number"
              min="0"
              step="0.01"
              value={form.pricePence / 100}
              onChange={(e) =>
                setForm({
                  ...form,
                  pricePence: Math.round(Number(e.target.value) * 100),
                })
              }
            />
          </label>
          <label>
            Customer minutes
            <input
              type="number"
              min="5"
              value={form.durationMinutes}
              onChange={(e) =>
                setForm({ ...form, durationMinutes: Number(e.target.value) })
              }
            />
          </label>
          <label>
            Cleanup buffer
            <input
              type="number"
              min="0"
              value={form.bufferMinutes}
              onChange={(e) =>
                setForm({ ...form, bufferMinutes: Number(e.target.value) })
              }
            />
          </label>
          <label>
            Deposit
            <select
              value={form.depositType}
              onChange={(e) =>
                setForm({ ...form, depositType: e.target.value })
              }
            >
              <option>NONE</option>
              <option>FIXED</option>
              <option>PERCENT</option>
            </select>
          </label>
          <label>
            Deposit value
            <input
              type="number"
              min="0"
              value={form.depositValue}
              onChange={(e) =>
                setForm({ ...form, depositValue: Number(e.target.value) })
              }
            />
          </label>
          <label>
            Online discount
            <select
              value={form.onlineDiscountType}
              onChange={(e) =>
                setForm({ ...form, onlineDiscountType: e.target.value })
              }
            >
              <option>NONE</option>
              <option>FIXED</option>
              <option>PERCENT</option>
            </select>
          </label>
          <label>
            {form.onlineDiscountType === "FIXED"
              ? "Online discount (£)"
              : form.onlineDiscountType === "PERCENT"
                ? "Online discount (%)"
                : "Discount value"}
            <input
              type="number"
              min="0"
              max={form.onlineDiscountType === "PERCENT" ? 100 : undefined}
              step={form.onlineDiscountType === "FIXED" ? "0.01" : "1"}
              value={
                form.onlineDiscountType === "FIXED"
                  ? form.onlineDiscountValue / 100
                  : form.onlineDiscountValue
              }
              onChange={(e) =>
                setForm({
                  ...form,
                  onlineDiscountValue:
                    form.onlineDiscountType === "FIXED"
                      ? Math.round(Number(e.target.value) * 100)
                      : Number(e.target.value),
                })
              }
            />
          </label>
          <label className="wide">
            Description
            <textarea
              value={form.description}
              onChange={(e) =>
                setForm({ ...form, description: e.target.value })
              }
            />
          </label>
          <button>Create service</button>
        </form>
      </section>
    </div>
  );
}

function Reports({ data }: any) {
  const completed = data.appointments.filter(
      (a: any) => a.status === "COMPLETED",
    ),
    cancelled = data.appointments.filter((a: any) => a.status === "CANCELLED"),
    revenue = completed.reduce(
      (sum: number, a: any) =>
        sum +
        Number(a.original_price_pence || 0) -
        Number(a.discount_pence || 0),
      0,
    );
  return (
    <>
      <div className="seat-grid">
        <div className="func-kpi">
          <span>Bookings shown</span>
          <strong>{data.appointments.length}</strong>
        </div>
        <div className="func-kpi">
          <span>Completed</span>
          <strong>{completed.length}</strong>
        </div>
        <div className="func-kpi">
          <span>Cancelled</span>
          <strong>{cancelled.length}</strong>
        </div>
        <div className="func-kpi">
          <span>Booked value</span>
          <strong>{pounds(revenue)}</strong>
        </div>
      </div>
      <section className="func-panel">
        <div className="func-table-head">
          <h2>Service performance</h2>
        </div>
        {data.services.map((s: any) => {
          const jobs = data.appointments.filter(
            (a: any) => a.service_id === s.id,
          );
          return (
            <article key={s.id}>
              <span>
                <b>{s.name}</b>
                <small>{jobs.length} bookings</small>
              </span>
              <em>
                {pounds(
                  jobs.reduce(
                    (n: number, a: any) =>
                      n + Number(a.original_price_pence || 0),
                    0,
                  ),
                )}
              </em>
            </article>
          );
        })}
      </section>
    </>
  );
}
function Settings({ data, settings, saved }: any) {
  const [f, setF] = useState({
    onlineBookingEnabled: Boolean(settings.online_booking_enabled),
    bookingIntervalMinutes: Number(settings.booking_interval_minutes || 15),
    changesCutoffHours: Number(settings.changes_cutoff_hours || 24),
    journey: settings.journey || "SERVICE_FIRST",
    allowAnyStaff: Boolean(settings.allow_any_staff),
  });
  async function submit(e: FormEvent) {
    e.preventDefault();
    await send("/api/appointments/settings", f, "PUT");
    saved();
  }
  return (
    <section className="func-panel">
      <div className="func-table-head">
        <h2>Online booking settings</h2>
        <a href="/book" target="_blank">
          Open public booking page
        </a>
      </div>
      <form className="appointment-form" onSubmit={submit}>
        <label className="check-row">
          <input
            type="checkbox"
            checked={f.onlineBookingEnabled}
            onChange={(e) =>
              setF({ ...f, onlineBookingEnabled: e.target.checked })
            }
          />
          Accept online bookings
        </label>
        <label className="check-row">
          <input
            type="checkbox"
            checked={f.allowAnyStaff}
            onChange={(e) => setF({ ...f, allowAnyStaff: e.target.checked })}
          />
          Allow “any available staff”
        </label>
        <label>
          Booking intervals (minutes)
          <input
            type="number"
            min="5"
            max="120"
            value={f.bookingIntervalMinutes}
            onChange={(e) =>
              setF({ ...f, bookingIntervalMinutes: Number(e.target.value) })
            }
          />
        </label>
        <label>
          Change/cancel cutoff (hours)
          <input
            type="number"
            min="0"
            max="720"
            value={f.changesCutoffHours}
            onChange={(e) =>
              setF({ ...f, changesCutoffHours: Number(e.target.value) })
            }
          />
        </label>
        <label>
          Booking journey
          <select
            value={f.journey}
            onChange={(e) => setF({ ...f, journey: e.target.value })}
          >
            <option value="SERVICE_FIRST">Choose service first</option>
            <option value="STAFF_FIRST">Choose staff first</option>
          </select>
        </label>
        <button>Save booking settings</button>
      </form>
      <LocationSettings data={data} saved={saved} />
      <WebsiteSettings data={data} saved={saved} />
      <TikTokSettings data={data} saved={saved} />
    </section>
  );
}

function WebsiteSettings({ data, saved }: any) {
  const locations = data.locations || [];
  const fromLocation = (location: any) => ({ name: location?.name || "", description: location?.description || "", phone: location?.phone || "", email: location?.email || "", address: location?.address || "", logoUrl: location?.logo_url || "", heroImageUrl: location?.hero_image_url || "", primaryColour: location?.primary_colour || "#142f38", accentColour: location?.accent_colour || "#b88967" });
  const [locationId, setLocationId] = useState(locations[0]?.id || "");
  const [form, setForm] = useState(() => fromLocation(locations[0]));
  const [error, setError] = useState("");
  function choose(value: string) { setLocationId(value); setForm(fromLocation(locations.find((x: any) => x.id === value))); setError(""); }
  async function submit(e: FormEvent) { e.preventDefault(); setError(""); try { await send(`/api/appointments/locations/${locationId}/website`, form, "PUT"); saved(); } catch (e: any) { setError(e.message); } }
  if (!locations.length) return null;
  const selected = locations.find((x: any) => x.id === locationId);
  return <div className="appointment-website-editor">
    <div className="func-table-head"><div><h2>Salon website</h2><p>Configure the public homepage without writing code.</p></div><a href={`https://${selected?.slug}.taskroute.co.uk/`} target="_blank" rel="noreferrer">Open homepage</a></div>
    <form className="appointment-form" onSubmit={submit}>
      <label className="wide">Salon location<select value={locationId} onChange={(e) => choose(e.target.value)}>{locations.map((x: any) => <option value={x.id} key={x.id}>{x.name} · {x.slug}.taskroute.co.uk</option>)}</select></label>
      <label>Public salon name<input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></label>
      <label>Telephone<input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></label>
      <label>Email address<input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></label>
      <label>Address<textarea value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} /></label>
      <label className="wide">Homepage introduction<textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Tell customers about this salon…" /></label>
      <label>Logo image HTTPS URL<input type="url" value={form.logoUrl} onChange={(e) => setForm({ ...form, logoUrl: e.target.value })} placeholder="https://…" /></label>
      <label>Hero photograph HTTPS URL<input type="url" value={form.heroImageUrl} onChange={(e) => setForm({ ...form, heroImageUrl: e.target.value })} placeholder="https://…" /></label>
      <label>Primary colour<input type="color" value={form.primaryColour} onChange={(e) => setForm({ ...form, primaryColour: e.target.value })} /></label>
      <label>Accent colour<input type="color" value={form.accentColour} onChange={(e) => setForm({ ...form, accentColour: e.target.value })} /></label>
      {error ? <p className="auth-error wide">{error}</p> : null}
      <button className="wide">Save salon homepage</button>
    </form>
  </div>;
}

function LocationSettings({ data, saved }: any) {
  const locations = data.locations || [];
  const limit = Number(data.businessSettings?.location_limit || 1);
  const canAdd = locations.length < limit;
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [error, setError] = useState("");
  async function submit(e: FormEvent) {
    e.preventDefault(); setError("");
    try { await send("/api/appointments/locations", { name, slug }); setName(""); setSlug(""); saved(); }
    catch (e: any) { setError(e.message); }
  }
  return <div className="appointment-location-editor">
    <div className="func-table-head"><div><h2>Salon locations</h2><p>Salon 1 uses the business address. Add Salon 2 and further locations here.</p></div></div>
    <div className="appointment-location-list">{locations.map((location: any, index: number) => <article key={location.id}><span><b>{location.name}</b><small>{index === 0 ? "Primary business and Salon 1 address" : `Salon ${index + 1}`}</small></span><a href={`https://${location.slug}.taskroute.co.uk/book`} target="_blank" rel="noreferrer">{location.slug}.taskroute.co.uk</a></article>)}</div>
    {canAdd ? <form className="appointment-form appointment-location-form" onSubmit={submit}>
      <label>New salon name<input required minLength={2} value={name} onChange={(e) => setName(e.target.value)} placeholder="Salon Two" /></label>
      <label>New salon address<div className="appointment-address-input"><input required pattern="[a-z0-9]+(?:-[a-z0-9]+)*" value={slug} onChange={(e) => setSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""))} placeholder="salontwo" /><span>.taskroute.co.uk</span></div></label>
      {error ? <p className="auth-error wide">{error}</p> : null}
      <button className="wide" disabled={!name || !slug}>Add salon location</button>
    </form> : <p className="appointment-location-limit">Your plan currently includes {limit} salon location{limit === 1 ? "" : "s"}. Contact TaskRoute to add another location.</p>}
  </div>;
}

function TikTokSettings({ data, saved }: any) {
  const locations = data.locations || [];
  const [locationId, setLocationId] = useState(locations[0]?.id || "");
  const profileFor = (value: string) => data.socialProfiles?.find((x: any) => x.location_id === value)?.social_profile_url || "";
  const videosFor = (value: string) => (data.socialVideos || []).filter((x: any) => x.location_id === value).map((x: any) => ({ title: x.title || "", url: x.source_url, visible: Boolean(x.visible) }));
  const [profileUrl, setProfileUrl] = useState(() => profileFor(locationId));
  const [videos, setVideos] = useState<any[]>(() => videosFor(locationId));
  const [error, setError] = useState("");
  function choose(value: string) {
    setLocationId(value); setProfileUrl(profileFor(value)); setVideos(videosFor(value)); setError("");
  }
  async function submit(e: FormEvent) {
    e.preventDefault(); setError("");
    try { await send("/api/appointments/website/embeds", { locationId, profileUrl, embeds: videos }, "PUT"); saved(); }
    catch (e: any) { setError(e.message); }
  }
  if (!locations.length) return <p className="func-empty">Add a salon location before configuring its website.</p>;
  return <div className="appointment-social-editor">
    <div className="func-table-head"><div><h2>Social and media embeds</h2><p>Add selected TikTok, YouTube, Instagram or Facebook content to this salon website.</p></div></div>
    <form className="appointment-form single" onSubmit={submit}>
      <label>Salon location<select value={locationId} onChange={(e) => choose(e.target.value)}>{locations.map((x: any) => <option value={x.id} key={x.id}>{x.name} · {x.slug}.taskroute.co.uk</option>)}</select></label>
      <label>Social profile link (optional)<input value={profileUrl} onChange={(e) => setProfileUrl(e.target.value)} placeholder="https://www.tiktok.com/@salonname" /></label>
      <div className="appointment-video-list">{videos.map((video, index) => <div className="appointment-video-row" key={index}>
        <label>Video title<input value={video.title} onChange={(e) => setVideos(videos.map((v, i) => i === index ? { ...v, title: e.target.value } : v))} placeholder="Balayage transformation" /></label>
        <label>Public post or video URL<input required type="url" value={video.url} onChange={(e) => setVideos(videos.map((v, i) => i === index ? { ...v, url: e.target.value } : v))} placeholder="TikTok, YouTube, Instagram or Facebook URL" /></label>
        <label className="check-row"><input type="checkbox" checked={video.visible} onChange={(e) => setVideos(videos.map((v, i) => i === index ? { ...v, visible: e.target.checked } : v))} />Show on website</label>
        <div className="appointment-video-actions"><button type="button" className="secondary" disabled={index === 0} onClick={() => setVideos(videos.map((v, i) => i === index ? videos[index - 1] : i === index - 1 ? video : v))}>Move up</button><button type="button" className="secondary danger" onClick={() => setVideos(videos.filter((_, i) => i !== index))}>Remove</button></div>
      </div>)}</div>
      {videos.length < 12 ? <button type="button" className="secondary" onClick={() => setVideos([...videos, { title: "", url: "", visible: true }])}>+ Add embed</button> : null}
      <small>Paste a full TikTok, YouTube, Instagram or Facebook HTTPS URL, not HTML code. Other providers are rejected.</small>
      {error ? <p className="auth-error">{error}</p> : null}
      <button disabled={!locationId}>Save media section</button>
    </form>
  </div>;
}
